import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { postWokDayCreate } from "../../api/profile/profileServices";
import { FiPlus, FiTrash2, FiChevronUp, FiChevronDown } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import "./workingDaysPage.css";
import toast from "react-hot-toast";

const daysList = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const defaultTimes = { start: "09:00AM", end: "06:00PM" };
const defaultTimesApi = { start_time: "9:00am", end_time: "6:00pm" };

const TIME_STEP = 5;
const MIN_GAP = 60;
const DEFAULT_RANGE_DURATION = 60;
const END_OF_DAY_MINUTES = 23 * 60 + 59;

const makeKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const parseTimeToDate = (timeStr) => {
  if (!timeStr) return null;

  const raw = String(timeStr).trim();

  if (/am|pm/i.test(raw)) {
    const timeStrLower = raw.toLowerCase();
    const [timePart, meridian] = [
      timeStrLower.slice(0, -2),
      timeStrLower.slice(-2),
    ];

    let [hours, minutes] = timePart.split(":").map(Number);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    if (meridian === "pm" && hours !== 12) {
      hours += 12;
    } else if (meridian === "am" && hours === 12) {
      hours = 0;
    }

    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  const [hours, minutes] = raw.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const toPm = (date) => {
  if (!date || !(date instanceof Date)) return "9:00am";

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const meridian = hours >= 12 ? "pm" : "am";

  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;

  return `${hours}:${minutes.toString().padStart(2, "0")}${meridian}`;
};

const timeToMinutes = (timeStr) => {
  const date = parseTimeToDate(timeStr);
  if (!date) return 0;
  return date.getHours() * 60 + date.getMinutes();
};

const minutesToTime = (mins) => {
  const safe = Math.max(0, Math.min(END_OF_DAY_MINUTES, mins));
  const date = new Date();
  date.setHours(Math.floor(safe / 60), safe % 60, 0, 0);
  return toPm(date).toUpperCase();
};

const convertTo24HourFormat = (timeStr) => {
  const date = parseTimeToDate(timeStr);
  if (!date) return "00:00:00";

  return `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}:00`;
};

const toAmPm = (time24) => {
  const date = parseTimeToDate(time24);
  if (!date) return "";
  return toPm(date).toUpperCase();
};

const to24 = (timeAmPm) => {
  const date = parseTimeToDate(timeAmPm);
  if (!date) return "00:00";

  return `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
};

const hasOverlap = (ranges) => {
  if (ranges.length <= 1) return false;

  const sorted = [...ranges].sort(
    (a, b) => timeToMinutes(a.start) - timeToMinutes(b.start),
  );

  for (let i = 0; i < sorted.length - 1; i++) {
    const endA = timeToMinutes(sorted[i].end);
    const startB = timeToMinutes(sorted[i + 1].start);

    if (endA > startB) return true;
  }

  return false;
};

const syncRangesForward = (ranges, changedIndex) => {
  const nextRanges = [...ranges];

  for (let i = changedIndex + 1; i < nextRanges.length; i++) {
    const prev = nextRanges[i - 1];
    const current = nextRanges[i];

    const prevEnd = timeToMinutes(prev.end);
    let currentStart = timeToMinutes(current.start);
    let currentEnd = timeToMinutes(current.end);

    const requiredStart = prevEnd + MIN_GAP;
    const currentDuration = Math.max(1, currentEnd - currentStart);

    if (currentStart < requiredStart) {
      currentStart = requiredStart;
      currentEnd = currentStart + currentDuration;
    }

    if (currentEnd > END_OF_DAY_MINUTES) {
      currentEnd = END_OF_DAY_MINUTES;
      currentStart = Math.max(requiredStart, currentEnd - currentDuration);
    }

    if (currentStart >= currentEnd) {
      currentStart = requiredStart;
      currentEnd = Math.min(
        currentStart + DEFAULT_RANGE_DURATION,
        END_OF_DAY_MINUTES,
      );
    }

    nextRanges[i] = {
      ...current,
      start: minutesToTime(currentStart),
      end: minutesToTime(currentEnd),
    };
  }

  return nextRanges;
};

const canAddTimeRangeByRanges = (ranges) => {
  if (!ranges?.length) return false;

  const lastRange = ranges[ranges.length - 1];
  const lastEnd = timeToMinutes(lastRange.end);
  const nextStart = lastEnd + MIN_GAP;

  return nextStart <= END_OF_DAY_MINUTES;
};

export default function WorkingDaysPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState("UTC");
  const userStatus = localStorage.getItem("user_status");

  const [schedule, setSchedule] = useState(
    daysList.map((dayKey) => ({
      key: dayKey,
      enabled: false,
      dayTimeRanges: [
        {
          _k: makeKey(),
          start: defaultTimes.start,
          end: defaultTimes.end,
          isBreak: false,
        },
      ],
    })),
  );

  const validateRangesWithBoundaries = (ranges) => {
    for (let i = 0; i < ranges.length; i++) {
      const current = ranges[i];
      const startMins = timeToMinutes(current.start);
      const endMins = timeToMinutes(current.end);

      if (endMins > END_OF_DAY_MINUTES || startMins > END_OF_DAY_MINUTES) {
        return t("workingDays.toast.errorPastMidnight");
      }

      if (endMins <= startMins) {
        return t("workingDays.toast.errorEndTime");
      }

      if (i > 0) {
        const prevEnd = timeToMinutes(ranges[i - 1].end);
        const minAllowedStart = prevEnd + MIN_GAP;

        if (startMins < minAllowedStart) {
          return t("workingDays.toast.errorSequence");
        }
      }
    }

    if (hasOverlap(ranges)) {
      return t("workingDays.toast.errorOverlap");
    }

    return null;
  };

  useEffect(() => {
    const timeZone = getUserTimeZone();
    setUserTimeZone(timeZone);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem("working_days_closed", "true");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    const workingDaysCode =
      userStatus === "salon" ? "230" : userStatus === "worker" ? "236" : "218";

    localStorage.setItem("onboarding_code", workingDaysCode);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [userStatus]);

  const toggleDay = (index) => {
    setSchedule((prev) =>
      prev.map((day, i) => {
        if (i !== index) return day;
        const newEnabled = !day.enabled;
        return {
          ...day,
          enabled: newEnabled,
          dayTimeRanges: newEnabled
            ? day.dayTimeRanges?.length
              ? day.dayTimeRanges
              : [
                  {
                    _k: makeKey(),
                    start: defaultTimes.start,
                    end: defaultTimes.end,
                    isBreak: false,
                  },
                ]
            : [],
        };
      }),
    );
  };

  const updateRangeValue = (dayIndex, rangeIndex, type, value) => {
    let showError = null;

    setSchedule((prev) => {
      const updated = [...prev];
      const selectedDay = { ...updated[dayIndex] };
      let newRanges = [...selectedDay.dayTimeRanges];

      newRanges[rangeIndex] = {
        ...newRanges[rangeIndex],
        [type]: value,
      };

      const current = newRanges[rangeIndex];
      const startTime = parseTimeToDate(current.start)?.getTime() || 0;
      const endTime = parseTimeToDate(current.end)?.getTime() || 0;
      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);
      const endOfDayTime = endOfDay.getTime();

      if (endTime > endOfDayTime || startTime > endOfDayTime) {
        showError = t("workingDays.toast.errorPastMidnight");
        return prev;
      }

      if (endTime <= startTime) {
        showError = t("workingDays.toast.errorEndTime");
        return prev;
      }

      if (rangeIndex > 0) {
        const prevEnd = timeToMinutes(newRanges[rangeIndex - 1].end);
        const minAllowedStart = prevEnd + MIN_GAP;

        if (
          type === "start" &&
          timeToMinutes(current.start) < minAllowedStart
        ) {
          showError = t("workingDays.toast.errorSequence");
          return prev;
        }
      }

      newRanges = syncRangesForward(newRanges, rangeIndex);

      const validationError = validateRangesWithBoundaries(newRanges);
      if (validationError) {
        showError = validationError;
        return prev;
      }

      selectedDay.dayTimeRanges = newRanges;
      updated[dayIndex] = selectedDay;
      return updated;
    });

    if (showError) {
      setTimeout(() => toast.error(showError), 0);
    }
  };

  const handleTimeChange = (dayIndex, rangeIndex, type, value24) => {
    const formatted = toAmPm(value24);
    if (!formatted) return;
    updateRangeValue(dayIndex, rangeIndex, type, formatted);
  };

  const shiftTime = (dayIndex, rangeIndex, type, deltaMinutes) => {
    let showError = null;

    setSchedule((prev) => {
      const updated = [...prev];
      const selectedDay = { ...updated[dayIndex] };
      let newRanges = [...selectedDay.dayTimeRanges];

      const current = newRanges[rangeIndex];
      const currentValue = type === "start" ? current.start : current.end;
      let nextMinutes = timeToMinutes(currentValue) + deltaMinutes;

      if (nextMinutes < 0) nextMinutes = 0;

      if (nextMinutes > END_OF_DAY_MINUTES) {
        showError = t("workingDays.toast.errorPastMidnight");
        return prev;
      }

      const nextFormatted = minutesToTime(nextMinutes);

      newRanges[rangeIndex] = {
        ...current,
        [type]: nextFormatted,
      };

      const changed = newRanges[rangeIndex];
      const changedStart = timeToMinutes(changed.start);
      const changedEnd = timeToMinutes(changed.end);

      if (changedEnd <= changedStart) {
        showError = t("workingDays.toast.errorEndTime");
        return prev;
      }

      if (rangeIndex > 0) {
        const prevEnd = timeToMinutes(newRanges[rangeIndex - 1].end);
        const minAllowedStart = prevEnd + MIN_GAP;

        if (type === "start" && changedStart < minAllowedStart) {
          showError = t("workingDays.toast.errorSequence");
          return prev;
        }
      }

      newRanges = syncRangesForward(newRanges, rangeIndex);

      const validationError = validateRangesWithBoundaries(newRanges);
      if (validationError) {
        showError = validationError;
        return prev;
      }

      selectedDay.dayTimeRanges = newRanges;
      updated[dayIndex] = selectedDay;
      return updated;
    });

    if (showError) {
      setTimeout(() => toast.error(showError), 0);
    }
  };

  const handleTimeKeyDown = (e, dayIndex, rangeIndex, type) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      shiftTime(dayIndex, rangeIndex, type, TIME_STEP);
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      shiftTime(dayIndex, rangeIndex, type, -TIME_STEP);
    }
  };

  const addTimeRange = (dayIndex) => {
    let showError = null;

    setSchedule((prev) => {
      const updated = [...prev];
      const selectedDay = { ...updated[dayIndex] };

      if (!selectedDay.enabled) {
        showError = t("workingDays.toast.errorEnableDay", {
          day: t(`workingDays.days.${selectedDay.key}`),
        });
        return prev;
      }

      const ranges = [...selectedDay.dayTimeRanges];
      if (!ranges.length) {
        showError = t("workingDays.toast.errorNoRanges");
        return prev;
      }

      const last = ranges[ranges.length - 1];
      const lastEnd = timeToMinutes(last.end);

      if (Number.isNaN(lastEnd)) {
        showError = t("workingDays.errorInvalidLastEnd");
        return prev;
      }

      const nextStart = lastEnd + MIN_GAP;
      if (nextStart > END_OF_DAY_MINUTES) {
        showError = t("workingDays.toast.errorReachedEndOfDay");
        return prev;
      }

      const nextEnd = Math.min(
        nextStart + DEFAULT_RANGE_DURATION,
        END_OF_DAY_MINUTES,
      );

      if (nextEnd <= nextStart) {
        showError = t("workingDays.toast.errorReachedEndOfDay");
        return prev;
      }

      const newRange = {
        _k: makeKey(),
        start: minutesToTime(nextStart),
        end: minutesToTime(nextEnd),
        isBreak: false,
      };

      const newRanges = [...ranges, newRange];
      const validationError = validateRangesWithBoundaries(newRanges);

      if (validationError) {
        showError = validationError;
        return prev;
      }

      selectedDay.dayTimeRanges = newRanges;
      updated[dayIndex] = selectedDay;
      return updated;
    });

    if (showError) {
      setTimeout(() => toast.error(showError), 0);
    }
  };

  const deleteTimeRange = (dayIndex, rangeIndex) => {
    setSchedule((prev) => {
      const updated = [...prev];
      const selectedDay = { ...updated[dayIndex] };

      if (selectedDay.dayTimeRanges.length <= 1) {
        selectedDay.dayTimeRanges = [
          {
            _k: makeKey(),
            start: defaultTimes.start,
            end: defaultTimes.end,
            isBreak: false,
          },
        ];
      } else {
        selectedDay.dayTimeRanges = selectedDay.dayTimeRanges.filter(
          (_, i) => i !== rangeIndex,
        );
      }

      updated[dayIndex] = selectedDay;
      return updated;
    });
  };

  const mergeActiveRanges = (ranges) => {
    const active = ranges.filter((r) => !r.isBreak);
    if (active.length === 0) return [];

    return active.map(({ start, end }) => ({ start, end }));
  };

  const createOrUpdateWorkdays = async () => {
    try {
      setLoading(true);

      const activeDays = schedule.filter((d) => d.enabled);

      if (userStatus !== "salon" && activeDays.length === 0) {
        toast.error(t("workingDays.toast.errorSelectAtLeastOneDay"));
        return;
      }

      const payload = schedule.map((day) => ({
        day: day.key,
        active: day.enabled,
        dayTimeRanges: day.enabled
          ? mergeActiveRanges(day.dayTimeRanges).map(({ start, end }) => ({
              start_time: convertTo24HourFormat(start),
              end_time: convertTo24HourFormat(end),
            }))
          : [],
      }));
      await postWokDayCreate(payload);

      localStorage.removeItem("working_days_closed");
      localStorage.setItem("working_days_saved_time", new Date().toISOString());
      localStorage.setItem("working_days_timezone", userTimeZone);

      if (userStatus === "worker") {
        localStorage.setItem("onboarding_code", "236");
        localStorage.setItem("onboarding_completed", "true");
        sessionStorage.setItem(
          "onboarding_force_route",
          "/finish-registration",
        );
        sessionStorage.setItem("onboarding_force_status", "worker");

        toast.success(t("workingDays.toast.successSaved"));
        navigate("/finish-registration", { replace: true });
        return;
      }

      const nextCode = userStatus === "salon" ? "232" : "220";
      localStorage.setItem("onboarding_code", nextCode);

      toast.success(t("workingDays.toast.successSaved"));
      navigate("/booking-settings", { replace: true });
    } catch (err) {
      console.error("Error saving workdays:", err);

      if (err?.response?.status === 400) {
        toast.error(t("workingDays.toast.error400"));
      } else if (err?.response?.status === 401) {
        toast.error(t("workingDays.toast.error401"));
      } else if (err?.response?.status === 500) {
        toast.error(t("workingDays.toast.error500"));
      } else {
        toast.error(t("workingDays.toast.errorGeneric"));
      }
    } finally {
      setLoading(false);
    }
  };

  const currentStep = userStatus === "worker" ? "2" : "4";
  const totalSteps = userStatus === "worker" ? "2" : "6";

  return (
    <div className="working-days-page">
      <div className="working-days-card">
        <div className="working-days-header">
          <div className="working-days-title">
            <h2>{t("workingDays.title")}</h2>
            <p>
              {userStatus === "salon"
                ? t("workingDays.subtitleSalon")
                : t("workingDays.subtitleDefault")}
            </p>
          </div>
        </div>

        <div className="working-days-list">
          {schedule.map((day, dayIndex) => (
            <div key={day.key} className="working-days-item">
              <span className="working-days-name">
                {t(`workingDays.days.${day.key}`)}
              </span>

              <div className="working-days-time-multi">
                {day.enabled ? (
                  day.dayTimeRanges.map((range, rangeIndex) => (
                    <div
                      key={range._k}
                      className="working-days-time"
                      data-label-start={t("workingDays.labelStart")}
                      data-label-end={t("workingDays.labelEnd")}
                    >
                      <div className="working-days-input">
                        <input
                          type="time"
                          value={to24(range.start)}
                          disabled={!day.enabled}
                          step={TIME_STEP * 60}
                          onKeyDown={(e) =>
                            handleTimeKeyDown(e, dayIndex, rangeIndex, "start")
                          }
                          onChange={(e) =>
                            handleTimeChange(
                              dayIndex,
                              rangeIndex,
                              "start",
                              e.target.value,
                            )
                          }
                        />

                        <div className="working-days-arrows">
                          <button
                            type="button"
                            className="working-days-arrow-btn"
                            disabled={!day.enabled}
                            onClick={() =>
                              shiftTime(
                                dayIndex,
                                rangeIndex,
                                "start",
                                TIME_STEP,
                              )
                            }
                          >
                            <FiChevronUp size={12} />
                          </button>
                          <button
                            type="button"
                            className="working-days-arrow-btn"
                            disabled={!day.enabled}
                            onClick={() =>
                              shiftTime(
                                dayIndex,
                                rangeIndex,
                                "start",
                                -TIME_STEP,
                              )
                            }
                          >
                            <FiChevronDown size={12} />
                          </button>
                        </div>
                      </div>

                      <span className="working-days-dash">–</span>

                      <div className="working-days-input">
                        <input
                          type="time"
                          value={to24(range.end)}
                          disabled={!day.enabled}
                          step={TIME_STEP * 60}
                          onKeyDown={(e) =>
                            handleTimeKeyDown(e, dayIndex, rangeIndex, "end")
                          }
                          onChange={(e) =>
                            handleTimeChange(
                              dayIndex,
                              rangeIndex,
                              "end",
                              e.target.value,
                            )
                          }
                        />

                        <div className="working-days-arrows">
                          <button
                            type="button"
                            className="working-days-arrow-btn"
                            disabled={!day.enabled}
                            onClick={() =>
                              shiftTime(dayIndex, rangeIndex, "end", TIME_STEP)
                            }
                          >
                            <FiChevronUp size={12} />
                          </button>
                          <button
                            type="button"
                            className="working-days-arrow-btn"
                            disabled={!day.enabled}
                            onClick={() =>
                              shiftTime(dayIndex, rangeIndex, "end", -TIME_STEP)
                            }
                          >
                            <FiChevronDown size={12} />
                          </button>
                        </div>
                      </div>

                      {rangeIndex === 0 ? (
                        <button
                          type="button"
                          className="add-time-btn"
                          onClick={() => addTimeRange(dayIndex)}
                          disabled={
                            !day.enabled ||
                            !canAddTimeRangeByRanges(day.dayTimeRanges)
                          }
                        >
                          <FiPlus size={16} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="delete-time-btn"
                          onClick={() => deleteTimeRange(dayIndex, rangeIndex)}
                          disabled={!day.enabled}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))
                ) : (
                  <span className="working-days-unavailable">
                    {t("workingDays.unavailable")}
                  </span>
                )}
              </div>

              <label className="working-days-toggle">
                <input
                  type="checkbox"
                  checked={day.enabled}
                  onChange={() => toggleDay(dayIndex)}
                />
                <span className="working-days-slider"></span>
              </label>
            </div>
          ))}
        </div>

        <div className="working-days-footer">
          <button
            className="working-days-next-btn"
            onClick={createOrUpdateWorkdays}
            disabled={loading}
          >
            {loading
              ? t("workingDays.btnSaving")
              : userStatus === "salon"
                ? t("workingDays.btnSkipSave")
                : t("workingDays.btnSave")}
          </button>

          <p className="working-days-step">
            {t("workingDays.stepText", {
              current: currentStep,
              total: totalSteps,
            })}{" "}
            {userStatus === "salon" && (
              <span className="text-muted">
                {t("workingDays.optionalForSalons")}
              </span>
            )}
          </p>
        </div>
      </div>

      <footer className="working-days-footer-info">
        <p>{t("workingDays.footerCopyright")}</p>
        <div className="working-days-footer-links">
          <a href="#">{t("workingDays.footerPrivacy")}</a>
          <a href="#">{t("workingDays.footerHelp")}</a>
        </div>
      </footer>
    </div>
  );
}
